import { EventData, GameResult, USCAEnd } from './types';

export interface TeamStats {
  event: string;
  team: string;
  hammerSuccess: number;
  hammerTotal: number;
  forceSuccess: number;
  forceTotal: number;
  stealSuccess: number;
  stealTotal: number;
  stealDefenseSuccess: number;
  stealDefenseTotal: number;
}

export interface WinLossStats {
  event: string;
  label: 'Winner' | 'Loser';
  hammerSuccess: number;
  hammerTotal: number;
  forceSuccess: number;
  forceTotal: number;
  stealSuccess: number;
  stealTotal: number;
  stealDefenseSuccess: number;
  stealDefenseTotal: number;
}

function computeTeamStatsForGame(game: GameResult, team: string): Omit<TeamStats, 'event' | 'team'> {
  let hammerSuccess = 0;
  let hammerTotal = 0;
  let forceSuccess = 0;
  let forceTotal = 0;
  let stealSuccess = 0;
  let stealTotal = 0;
  let stealDefenseSuccess = 0;
  let stealDefenseTotal = 0;

  for (const end of game.ends) {
    const hasHammer = end.hammerTeam === team;
    const isBlank = end.hammerScore === 0 && end.nonHammerScore === 0;
    const wasStolen = end.nonHammerScore > 0;

    if (hasHammer) {
      // Hammer Efficiency: scored 2+ with hammer in non-blank ends
      if (!isBlank) {
        hammerTotal++;
        if (end.hammerScore >= 2) {
          hammerSuccess++;
        }
      }

      // Steal Defense: ends stolen against / ends with hammer (blanks included)
      stealDefenseTotal++;
      if (wasStolen) {
        stealDefenseSuccess++;
      }
    } else {
      // Without hammer (team is the non-hammer team)

      // Steal Efficiency: ends stolen / total ends without hammer (blanks included)
      stealTotal++;
      if (wasStolen) {
        // Non-hammer team scored = steal for this team
        stealSuccess++;
      }

      // Force Efficiency: opponent scored 1 with hammer / total ends opponent scored with hammer
      // (not blank, not stolen)
      if (!isBlank && !wasStolen) {
        // Opponent scored with their hammer
        forceTotal++;
        if (end.hammerScore === 1) {
          forceSuccess++;
        }
      }
    }
  }

  return {
    hammerSuccess,
    hammerTotal,
    forceSuccess,
    forceTotal,
    stealSuccess,
    stealTotal,
    stealDefenseSuccess,
    stealDefenseTotal,
  };
}

export function computeByTeam(eventData: EventData): TeamStats[] {
  const teamMap = new Map<string, TeamStats>();
  const eventName = eventData.eventName || String(eventData.eventId);

  for (const game of eventData.games) {
    const teams = game.teams.map(t => t.team);

    for (const team of teams) {
      const stats = computeTeamStatsForGame(game, team);

      if (!teamMap.has(team)) {
        teamMap.set(team, {
          event: eventName,
          team,
          hammerSuccess: 0,
          hammerTotal: 0,
          forceSuccess: 0,
          forceTotal: 0,
          stealSuccess: 0,
          stealTotal: 0,
          stealDefenseSuccess: 0,
          stealDefenseTotal: 0,
        });
      }

      const existing = teamMap.get(team)!;
      existing.hammerSuccess += stats.hammerSuccess;
      existing.hammerTotal += stats.hammerTotal;
      existing.forceSuccess += stats.forceSuccess;
      existing.forceTotal += stats.forceTotal;
      existing.stealSuccess += stats.stealSuccess;
      existing.stealTotal += stats.stealTotal;
      existing.stealDefenseSuccess += stats.stealDefenseSuccess;
      existing.stealDefenseTotal += stats.stealDefenseTotal;
    }
  }

  return Array.from(teamMap.values()).sort((a, b) => a.team.localeCompare(b.team));
}

export function computeByWinLoss(eventData: EventData): WinLossStats[] {
  const eventName = eventData.eventName || String(eventData.eventId);

  const winnerStats: WinLossStats = {
    event: eventName,
    label: 'Winner',
    hammerSuccess: 0,
    hammerTotal: 0,
    forceSuccess: 0,
    forceTotal: 0,
    stealSuccess: 0,
    stealTotal: 0,
    stealDefenseSuccess: 0,
    stealDefenseTotal: 0,
  };

  const loserStats: WinLossStats = {
    event: eventName,
    label: 'Loser',
    hammerSuccess: 0,
    hammerTotal: 0,
    forceSuccess: 0,
    forceTotal: 0,
    stealSuccess: 0,
    stealTotal: 0,
    stealDefenseSuccess: 0,
    stealDefenseTotal: 0,
  };

  for (const game of eventData.games) {
    if (game.teams.length < 2) continue;

    // Determine winner by total score
    const sorted = [...game.teams].sort((a, b) => b.total - a.total);
    if (sorted[0].total === sorted[1].total) {
      // Tie — skip
      continue;
    }

    const winner = sorted[0].team;
    const loser = sorted[1].team;

    const winStats = computeTeamStatsForGame(game, winner);
    const loseStats = computeTeamStatsForGame(game, loser);

    winnerStats.hammerSuccess += winStats.hammerSuccess;
    winnerStats.hammerTotal += winStats.hammerTotal;
    winnerStats.forceSuccess += winStats.forceSuccess;
    winnerStats.forceTotal += winStats.forceTotal;
    winnerStats.stealSuccess += winStats.stealSuccess;
    winnerStats.stealTotal += winStats.stealTotal;
    winnerStats.stealDefenseSuccess += winStats.stealDefenseSuccess;
    winnerStats.stealDefenseTotal += winStats.stealDefenseTotal;

    loserStats.hammerSuccess += loseStats.hammerSuccess;
    loserStats.hammerTotal += loseStats.hammerTotal;
    loserStats.forceSuccess += loseStats.forceSuccess;
    loserStats.forceTotal += loseStats.forceTotal;
    loserStats.stealSuccess += loseStats.stealSuccess;
    loserStats.stealTotal += loseStats.stealTotal;
    loserStats.stealDefenseSuccess += loseStats.stealDefenseSuccess;
    loserStats.stealDefenseTotal += loseStats.stealDefenseTotal;
  }

  return [winnerStats, loserStats];
}

export function teamStatsToCsv(stats: TeamStats[]): string {
  const header = 'Event,Team,Hammer Success,Hammer Total,Force Success,Force Total,Steal Success,Steal Total,Steal Defense Success,Steal Defense Total';
  const rows = stats.map(s =>
    `${csvEscape(s.event)},${csvEscape(s.team)},${s.hammerSuccess},${s.hammerTotal},${s.forceSuccess},${s.forceTotal},${s.stealSuccess},${s.stealTotal},${s.stealDefenseSuccess},${s.stealDefenseTotal}`
  );
  return [header, ...rows].join('\n');
}

export function winLossStatsToCsv(stats: WinLossStats[]): string {
  const header = 'Event,Winner/Loser,Hammer Success,Hammer Total,Force Success,Force Total,Steal Success,Steal Total,Steal Defense Success,Steal Defense Total';
  const rows = stats.map(s =>
    `${csvEscape(s.event)},${s.label},${s.hammerSuccess},${s.hammerTotal},${s.forceSuccess},${s.forceTotal},${s.stealSuccess},${s.stealTotal},${s.stealDefenseSuccess},${s.stealDefenseTotal}`
  );
  return [header, ...rows].join('\n');
}

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
